import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import AdminAppointments from "./AdminAppointments";
import AdminServices from "./AdminServices";
import AdminUsers from "./AdminUsers";
import AdminQrScanner from "./AdminQrScanner";
import PetInfoModal from "./PetInfoModal";
import AdminSupportInbox from "./AdminSupportInbox";
import AdminAuditLogs from "./AdminAuditLogs";
import AdminForecasting from "./AdminForecasting";
import AdminProducts from "./AdminProducts";
import AdminShowcase from "./AdminShowcase";
import {
  CalendarIcon, ScissorsIcon, UserIcon,
  PawIcon, ClockIcon, CheckCircleIcon
} from "./Icons";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";

/* Inline local admin dashboard icons */
const ChartIcon = ({ size = 18, color = "currentColor", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const MessageIcon = ({ size = 18, color = "currentColor", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const SparklesLocalIcon = ({ size = 18, color = "currentColor", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
    <path d="M5 3v4"/>
    <path d="M19 17v4"/>
    <path d="M3 5h4"/>
    <path d="M17 19h4"/>
  </svg>
);


const ListIcon = ({ size = 18, color = "currentColor", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

const CameraIcon = ({ size = 18, color = "currentColor", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

const BellIcon = ({ size = 20, color = "currentColor", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const TrendIcon = ({ size = 18, color = "currentColor", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const CoinsIcon = ({ size = 20, color = "currentColor", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const ShopIcon = ({ size = 18, color = "currentColor", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </svg>
);

const playNotificationSound = () => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const playNote = (freq, startOffset, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, ctx.currentTime + startOffset);
      gain.gain.setValueAtTime(0.1, ctx.currentTime + startOffset);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startOffset + duration);
      osc.start(ctx.currentTime + startOffset);
      osc.stop(ctx.currentTime + startOffset + duration);
    };
    playNote(880, 0, 0.2);
    playNote(1046.5, 0.22, 0.3);
    setTimeout(() => { ctx.close().catch(() => {}); }, 700);
  } catch (e) {}
};

// ── Custom Tooltip for Bar Chart ──────────────────────────────────────────────
const IncomeTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-[#e1ebf0] rounded-2xl shadow-xl px-4 py-3">
        <p className="text-xs font-black text-[#8a7658] uppercase tracking-widest mb-1">{label}</p>
        <p className="text-lg font-black text-[#46735c]">₱{payload[0].value.toLocaleString()}</p>
        <p className="text-xs text-gray-400">{payload[1]?.value || 0} appointment{payload[1]?.value !== 1 ? "s" : ""}</p>
      </div>
    );
  }
  return null;
};

// ── Service Pie Tooltip ────────────────────────────────────────────────────────
const ServiceTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-[#e1ebf0] rounded-2xl shadow-xl px-4 py-3">
        <p className="text-xs font-black text-gray-600 mb-1">{payload[0].name}</p>
        <p className="text-base font-black" style={{ color: payload[0].payload.fill }}>
          {payload[0].value} booking{payload[0].value !== 1 ? "s" : ""}
        </p>
      </div>
    );
  }
  return null;
};

// ── Admin Overview Dashboard ──────────────────────────────────────────────────
const AdminOverview = ({ appointments, isAdmin, onOpenDetails, onApprove, onOpenReject }) => {
  const now = new Date();
  const [chartView, setChartView] = React.useState("monthly");
  const [customDate, setCustomDate] = React.useState("");
  const [calendarDate, setCalendarDate] = React.useState(new Date());

  // ── Pending Appointments ──
  const pendingApts = appointments.filter(a => a.status === "pending");

  // ── CUSTOM DATE lookup ──
  const customDateApts = customDate
    ? appointments.filter(a => {
        const ap = new Date(a.createdAt);
        const [yr, mo, dy] = customDate.split("-").map(Number);
        return (
          ap.getFullYear() === yr &&
          ap.getMonth() + 1 === mo &&
          ap.getDate() === dy &&
          a.status === "completed"
        );
      })
    : [];
  const customDateRevenue = customDateApts.reduce((s, a) => s + (a.totalPrice || 0), 0);

  // ── DAILY: last 30 days ──
  const dailyData = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
    const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const dayApts = appointments.filter(a => {
      const ap = new Date(a.createdAt);
      return (
        ap.getFullYear() === d.getFullYear() &&
        ap.getMonth() === d.getMonth() &&
        ap.getDate() === d.getDate() &&
        a.status === "completed"
      );
    });
    dailyData.push({
      label,
      income: dayApts.reduce((s, a) => s + (a.totalPrice || 0), 0),
      count: dayApts.length,
      isCurrent: i === 0,
    });
  }

  // ── MONTHLY: last 6 months ──
  const monthlyData = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const label = d.toLocaleDateString("en-US", { month: "long" });
    const monthApts = appointments.filter(a => {
      const ap = new Date(a.createdAt);
      return (
        ap.getFullYear() === d.getFullYear() &&
        ap.getMonth() === d.getMonth() &&
        a.status === "completed"
      );
    });
    monthlyData.push({
      label,
      income: monthApts.reduce((s, a) => s + (a.totalPrice || 0), 0),
      count: monthApts.length,
      isCurrent: i === 0,
    });
  }

  // ── YEARLY: last 5 years ──
  const yearlyData = [];
  for (let i = 4; i >= 0; i--) {
    const yr = now.getFullYear() - i;
    const label = String(yr);
    const yearApts = appointments.filter(a => {
      const ap = new Date(a.createdAt);
      return ap.getFullYear() === yr && a.status === "completed";
    });
    yearlyData.push({
      label,
      income: yearApts.reduce((s, a) => s + (a.totalPrice || 0), 0),
      count: yearApts.length,
      isCurrent: i === 0,
    });
  }

  const chartData = chartView === "daily" ? dailyData : chartView === "monthly" ? monthlyData : yearlyData;
  const chartSubtitle = chartView === "daily"
    ? "Revenue from completed appointments — last 30 days"
    : chartView === "monthly"
    ? "Revenue from completed appointments — last 6 months"
    : "Revenue from completed appointments — last 5 years";

  // ── Compute this month stats ──
  const thisMonthApts = appointments.filter(a => {
    const ap = new Date(a.createdAt);
    return ap.getFullYear() === now.getFullYear() && ap.getMonth() === now.getMonth();
  });
  const thisMonthRevenue = thisMonthApts
    .filter(a => a.status === "completed")
    .reduce((s, a) => s + (a.totalPrice || 0), 0);

  // ── Compute today's revenue ──
  const todayRevenue = appointments
    .filter(a => {
      if (a.status !== "completed") return false;
      const ap = new Date(a.createdAt);
      return (
        ap.getFullYear() === now.getFullYear() &&
        ap.getMonth() === now.getMonth() &&
        ap.getDate() === now.getDate()
      );
    })
    .reduce((s, a) => s + (a.totalPrice || 0), 0);

  const totalRevenue = appointments
    .filter(a => a.status === "completed")
    .reduce((s, a) => s + (a.totalPrice || 0), 0);

  const pending   = appointments.filter(a => a.status === "pending").length;
  const completed = appointments.filter(a => a.status === "completed").length;
  const requests  = appointments.filter(a => a.status.includes("requested")).length;

  const serviceCount = {
    "Full Grooming": 0,
    "Basic Grooming": 0,
    "Small Room": 0,
    "Large Room": 0,
    "Cat Villa Small": 0,
    "Cat Villa Large": 0,
    "Other": 0
  };
  
  appointments.forEach(a => {
    (a.pets || []).forEach(p => {
      let matched = false;
      if (p.serviceType === "grooming" || p.serviceType === "both") {
        const gService = p.grooming?.service;
        if (gService === "Full Grooming") {
          serviceCount["Full Grooming"]++;
          matched = true;
        } else if (gService === "Basic Grooming" || gService === "Cat Grooming") {
          serviceCount["Basic Grooming"]++;
          matched = true;
        }
      }
      if (p.serviceType === "hotel" || p.serviceType === "both") {
        const rType = p.hotel?.roomType;
        if (rType === "Cat Villa Small") {
          serviceCount["Cat Villa Small"]++;
          matched = true;
        } else if (rType === "Cat Villa Large") {
          serviceCount["Cat Villa Large"]++;
          matched = true;
        } else if (rType === "Cat Villa") {
          // legacy booking support
          serviceCount["Cat Villa Small"]++;
          matched = true;
        } else if (rType === "Dog Room Small") {
          serviceCount["Small Room"]++;
          matched = true;
        } else if (rType === "Dog Room Large") {
          serviceCount["Large Room"]++;
          matched = true;
        }
      }
      if (!matched && p.serviceType) {
        serviceCount["Other"]++;
      }
    });
  });

  // Filter out any 0 values
  Object.keys(serviceCount).forEach(k => {
    if (serviceCount[k] === 0) delete serviceCount[k];
  });
  const PIE_COLORS = ["#6992a5", "#46735c", "#e0a458", "#a78bfa", "#f472b6", "#34d399"];
  const pieData = Object.entries(serviceCount).map(([name, value], i) => ({
    name,
    value,
    fill: PIE_COLORS[i % PIE_COLORS.length],
  }));

  // ── Recent completed appointments ──
  const recent = appointments
    .filter(a => a.status === "completed")
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5);

  const statCards = [
    {
      label: "Today's Revenue",
      value: `₱${todayRevenue.toLocaleString()}`,
      Icon: TrendIcon,
      color: "from-rose-400 to-pink-500",
      bg: "bg-rose-50",
      text: "text-rose-700",
      sub: new Date().toLocaleDateString("en-PH", { weekday: "long", month: "short", day: "numeric" }),
    },
    {
      label: "Total Revenue",
      value: `₱${totalRevenue.toLocaleString()}`,
      Icon: CoinsIcon,
      color: "from-emerald-500 to-teal-500",
      bg: "bg-emerald-50",
      text: "text-emerald-700",
    },
    {
      label: "This Month",
      value: `₱${thisMonthRevenue.toLocaleString()}`,
      Icon: CalendarIcon,
      color: "from-blue-500 to-indigo-500",
      bg: "bg-blue-50",
      text: "text-blue-700",
    },
    {
      label: "Completed",
      value: completed,
      Icon: CheckCircleIcon,
      color: "from-purple-500 to-pink-500",
      bg: "bg-purple-50",
      text: "text-purple-700",
    },
    {
      label: "Pending",
      value: pending,
      Icon: ClockIcon,
      color: "from-amber-400 to-orange-400",
      bg: "bg-amber-50",
      text: "text-amber-700",
    },
  ];

  if (requests > 0) {
    statCards.push({
      label: "Action Required",
      value: requests,
      Icon: BellIcon,
      color: "from-red-400 to-rose-500",
      bg: "bg-red-50",
      text: "text-red-700",
    });
  }

  // ── Calendar Helper Logic for Overview ──
  const calYear = calendarDate.getFullYear();
  const calMonth = calendarDate.getMonth();
  const calFirstDay = new Date(calYear, calMonth, 1).getDay();
  const calDaysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  
  const calDays = Array(calFirstDay).fill(null);
  for (let i = 1; i <= calDaysInMonth; i++) {
    calDays.push(new Date(calYear, calMonth, i));
  }
  
  const calMonthName = calendarDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const calPrevMonth = () => setCalendarDate(new Date(calYear, calMonth - 1, 1));
  const calNextMonth = () => setCalendarDate(new Date(calYear, calMonth + 1, 1));
  const todayDate = new Date();

  return (
    <div className="space-y-8">

      {/* ── Stat Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {statCards.map((card, i) => (
          <div key={i} className={`${card.bg} rounded-2xl p-5 border border-white shadow-sm relative overflow-hidden`}>
            <div className={`absolute -top-3 -right-3 w-16 h-16 rounded-full bg-gradient-to-br ${card.color} opacity-10`} />
            <div className="mb-2">
              <card.Icon size={24} className={card.text} />
            </div>
            <p className={`text-2xl font-black ${card.text}`}>{card.value}</p>
            <p className="text-xs font-bold text-gray-500 mt-1 uppercase tracking-widest">{card.label}</p>
            {card.sub && (
              <p className={`text-[10px] font-semibold mt-0.5 ${card.text} opacity-70`}>{card.sub}</p>
            )}
          </div>
        ))}
      </div>

      {/* ── ⚡ PENDING BOOKINGS REQUIRING APPROVAL ── */}
      <div className="bg-white rounded-3xl border-2 border-amber-200/80 shadow-md p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-100 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-xl shadow-sm">
              ⏳
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-black text-gray-800">Pending Bookings Requiring Approval</h3>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-black bg-amber-500 text-white shadow-sm">
                  {pendingApts.length}
                </span>
              </div>
              <p className="text-xs text-gray-400 font-medium">Approve or reject customer bookings directly without switching tabs</p>
            </div>
          </div>
          {pendingApts.length > 0 && (
            <span className="text-xs font-black text-amber-700 bg-amber-50 px-3 py-1.5 rounded-xl border border-amber-200 self-start sm:self-auto flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping" /> Action Needed
            </span>
          )}
        </div>

        {pendingApts.length === 0 ? (
          <div className="text-center py-10 bg-emerald-50/40 rounded-2xl border border-emerald-100">
            <p className="text-3xl mb-2">✨</p>
            <p className="text-sm font-bold text-emerald-800">All caught up! No pending bookings waiting for review.</p>
            <p className="text-xs text-emerald-600/80 mt-1">New booking requests submitted by customers will instantly pop up here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {pendingApts.map(apt => (
              <div key={apt._id} className="bg-[#fcfbf9] border border-amber-200 rounded-2xl p-5 hover:shadow-md transition-all flex flex-col justify-between space-y-4">
                <div>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <h4 className="font-bold text-gray-800 text-base flex items-center gap-2">
                        {apt.ownerName}
                        {apt.isWalkIn && <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-[9px] rounded-full uppercase tracking-wider font-bold">Walk-In</span>}
                      </h4>
                      <p className="text-xs text-gray-500 font-medium">{apt.ownerPhone} · {apt.ownerEmail}</p>
                    </div>
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 font-black text-[10px] uppercase rounded-full tracking-wider shrink-0">
                      Pending
                    </span>
                  </div>

                  {/* Pets in booking */}
                  <div className="space-y-1.5 my-3">
                    {(apt.pets || []).map((p, idx) => (
                      <div key={idx} className="bg-white p-2.5 rounded-xl border border-gray-100 flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <span className="text-base">{p.petType === "cat" ? "🐱" : "🐶"}</span>
                          <div>
                            <p className="font-bold text-gray-800">{p.petName}</p>
                            <p className="text-gray-500 capitalize">{p.serviceType === "both" ? "Hotel & Grooming" : p.serviceType} {p.grooming?.service ? `· ${p.grooming.service}` : ""}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-700">
                            {p.grooming?.date ? new Date(p.grooming.date).toLocaleDateString("en-PH", { month: "short", day: "numeric" }) : (p.hotel?.checkInDate ? new Date(p.hotel.checkInDate).toLocaleDateString("en-PH", { month: "short", day: "numeric" }) : "N/A")}
                          </p>
                          <p className="text-[10px] text-gray-400 font-semibold">{p.grooming?.time || p.hotel?.checkInTime || ""}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Payment Summary */}
                  <div className="flex items-center justify-between bg-white px-3 py-2 rounded-xl border border-gray-100 text-xs">
                    <span className="text-gray-500">
                      Payment: <strong className="text-gray-800">{apt.paymentMethod || "Cash"}</strong> {apt.downpaymentAmount > 0 ? `(₱${apt.downpaymentAmount.toLocaleString()} DP)` : ""}
                    </span>
                    <span className="font-black text-sm text-[#46735c]">Total: ₱{(apt.totalPrice || 0).toLocaleString()}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                  <button
                    onClick={() => onOpenDetails(apt)}
                    className="flex-1 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold text-xs rounded-xl transition-colors text-center"
                  >
                    Details
                  </button>
                  <button
                    onClick={() => onApprove(apt)}
                    className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-sm transition-colors text-center"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => onOpenReject(apt)}
                    className="flex-1 py-2.5 bg-red-50 hover:bg-red-600 text-red-600 hover:text-white font-bold text-xs rounded-xl transition-colors border border-red-200 text-center"
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── 📅 BOOKING CALENDAR IN OVERVIEW ── */}
      <div className="bg-white p-5 sm:p-6 rounded-3xl border border-[#e1ebf0] shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-4">
          <div>
            <h3 className="text-lg font-black text-gray-800 flex items-center gap-2">
              <span>📅</span> Appointment Calendar
            </h3>
            <p className="text-xs text-gray-400 font-semibold">Scheduled bookings by date — click any booking to view details &amp; take actions</p>
          </div>
          <div className="flex items-center gap-2 bg-gray-50 p-1.5 rounded-2xl border border-gray-100">
            <button onClick={calPrevMonth} className="px-3 py-1.5 bg-white hover:bg-gray-100 rounded-xl text-gray-700 font-bold transition-colors text-xs shadow-sm">&larr; Prev</button>
            <span className="text-xs font-black text-gray-800 min-w-[130px] text-center">{calMonthName}</span>
            <button onClick={calNextMonth} className="px-3 py-1.5 bg-white hover:bg-gray-100 rounded-xl text-gray-700 font-bold transition-colors text-xs shadow-sm">Next &rarr;</button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-1">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
            <div key={d} className="text-center text-xs font-black text-[#6992a5] uppercase py-1.5">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {calDays.map((dateObj, i) => {
            if (!dateObj) return <div key={`cal-empty-${i}`} className="min-h-[90px] sm:min-h-[110px] bg-gray-50/40 rounded-2xl border border-gray-100 hidden sm:block"></div>;

            const dayApts = appointments.filter(a => {
              let aptDate = null;
              if (a.pets && a.pets.length > 0) {
                const dates = a.pets.map(p => {
                  if (p.serviceType === "hotel" && p.hotel?.checkInDate) return new Date(p.hotel.checkInDate);
                  if (p.grooming?.date) return new Date(p.grooming.date);
                  return null;
                }).filter(Boolean);
                if (dates.length > 0) {
                  aptDate = new Date(Math.min(...dates.map(d => d.getTime())));
                }
              }
              if (!aptDate) aptDate = new Date(a.createdAt);

              return aptDate.getFullYear() === dateObj.getFullYear() &&
                     aptDate.getMonth() === dateObj.getMonth() &&
                     aptDate.getDate() === dateObj.getDate();
            });

            const isToday = todayDate.toDateString() === dateObj.toDateString();
            const isClosed = dateObj.getDay() === 1;

            return (
              <div key={i} className={`min-h-[90px] sm:min-h-[110px] p-1.5 sm:p-2 rounded-2xl border ${isToday ? "border-[#6992a5] bg-[#6992a5]/5" : isClosed ? "border-amber-100 bg-amber-50/20" : "border-gray-100 bg-white"} flex flex-col`}>
                <div className="flex justify-between items-center mb-1">
                  {isClosed ? (
                    <span className="text-[8px] sm:text-[9px] font-black text-amber-600 uppercase tracking-wider">Closed</span>
                  ) : <span />}
                  <span className={`text-xs font-bold ${isToday ? "text-[#6992a5] font-black" : "text-gray-400"}`}>
                    {dateObj.getDate()}
                  </span>
                </div>
                <div className="flex-1 flex flex-col gap-1 overflow-y-auto max-h-[70px] sm:max-h-[85px] no-scrollbar">
                  {dayApts.map(apt => {
                    let colorClass = "bg-gray-100 text-gray-600";
                    if (apt.status === "pending") colorClass = "bg-amber-100 text-amber-800 border border-amber-200";
                    else if (apt.status === "confirmed") colorClass = "bg-blue-100 text-blue-800 border border-blue-200";
                    else if (apt.status === "completed") colorClass = "bg-emerald-100 text-emerald-800 border border-emerald-200";
                    else if (apt.status === "rejected" || apt.status === "cancelled") colorClass = "bg-red-100 text-red-700 border border-red-200";
                    else if (apt.status.includes("requested")) colorClass = "bg-orange-100 text-orange-700 animate-pulse border border-orange-200";

                    return (
                      <div 
                        key={apt._id} 
                        onClick={() => onOpenDetails(apt)}
                        className={`text-[9px] sm:text-[10px] p-1 rounded-lg truncate cursor-pointer hover:opacity-80 font-bold ${colorClass}`}
                        title={`${apt.ownerName} - ${apt.status.replace("_", " ")}`}
                      >
                        {apt.ownerName.split(" ")[0]} ({apt.status})
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Income Bar Chart ── */}
      <div className="bg-white rounded-3xl border border-[#e1ebf0] shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-black text-gray-800">Income Overview</h3>
            <p className="text-xs text-gray-400 mt-0.5">{chartSubtitle}</p>
          </div>
          {/* Toggle buttons */}
          <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl">
            {[
              { id: "daily",   label: "Daily"   },
              { id: "monthly", label: "Monthly" },
              { id: "yearly",  label: "Yearly"  },
            ].map(opt => (
              <button
                key={opt.id}
                onClick={() => setChartView(opt.id)}
                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  chartView === opt.id
                    ? "bg-[#6992a5] text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: chartView === "daily" ? 9 : 11, fontWeight: 700, fill: "#8a7658" }}
              axisLine={false}
              tickLine={false}
              interval={chartView === "daily" ? 4 : 0}
            />
            <YAxis
              tick={{ fontSize: 11, fontWeight: 700, fill: "#8a7658" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={v => `₱${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`}
              width={50}
            />
            <Tooltip content={<IncomeTooltip />} cursor={{ fill: "#f8fafb", radius: 8 }} />
            <Bar dataKey="income" name="Income" radius={[8, 8, 0, 0]} maxBarSize={chartView === "daily" ? 16 : 60}>
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.isCurrent ? "#46735c" : "#6992a5"}
                />
              ))}
            </Bar>
            <Bar dataKey="count" name="count" hide />
          </BarChart>
        </ResponsiveContainer>
        <p className="text-center text-xs text-gray-400 mt-2">
          Current {chartView === "daily" ? "day" : chartView === "monthly" ? "month" : "year"} highlighted in{" "}
          <span className="font-bold text-[#46735c]">green</span>
        </p>
      </div>

      {/* ── Date Income Lookup ── */}
      <div className="bg-white rounded-3xl border border-[#e1ebf0] shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <div>
            <h3 className="text-lg font-black text-gray-800">📅 Date Income Lookup</h3>
            <p className="text-xs text-gray-400 mt-0.5">Pick any date to see the income and appointments for that day</p>
          </div>
          <input
            type="date"
            value={customDate}
            max={new Date().toISOString().split("T")[0]}
            onChange={e => setCustomDate(e.target.value)}
            className="px-4 py-2.5 border-2 border-[#e1ebf0] rounded-xl text-sm font-bold text-gray-700 outline-none focus:border-[#6992a5] transition-colors bg-gray-50 focus:bg-white"
          />
        </div>

        {!customDate && (
          <div className="flex flex-col items-center justify-center py-10 text-gray-300">
            <span className="text-5xl mb-3">📆</span>
            <p className="text-sm font-bold">Select a date above to view income details</p>
          </div>
        )}

        {customDate && (
          <>
            {/* Summary strip */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-5">
              <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-100">
                <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-1">Income</p>
                <p className="text-2xl font-black text-emerald-700">₱{customDateRevenue.toLocaleString()}</p>
              </div>
              <div className="bg-blue-50 rounded-2xl p-4 border border-blue-100">
                <p className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-1">Completed</p>
                <p className="text-2xl font-black text-blue-700">{customDateApts.length}</p>
              </div>
              <div className="sm:col-span-1 col-span-2 bg-[#f8fafb] rounded-2xl p-4 border border-[#e1ebf0] flex items-center gap-3">
                <span className="text-3xl">🗓️</span>
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Selected Date</p>
                  <p className="text-sm font-black text-gray-700">
                    {new Date(customDate + "T00:00:00").toLocaleDateString("en-PH", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                  </p>
                </div>
              </div>
            </div>

            {/* Appointment list */}
            {customDateApts.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-2xl border border-gray-100">
                <p className="text-4xl mb-2">😴</p>
                <p className="text-sm font-bold text-gray-400">No completed appointments on this date</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3">Appointments</p>
                {customDateApts.map((apt, i) => (
                  <div key={apt._id || i} className="flex items-center justify-between p-4 rounded-2xl bg-[#f8fafb] border border-[#e1ebf0]">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#6992a5] to-[#46735c] flex items-center justify-center text-white text-sm font-black flex-shrink-0">
                        {apt.ownerName?.charAt(0) || "?"}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-800">{apt.ownerName}</p>
                        <p className="text-xs text-gray-400">
                          {(apt.pets || []).map(p => `${p.petName} (${p.serviceType})`).join(" · ")}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm font-black text-[#46735c] whitespace-nowrap ml-4">₱{(apt.totalPrice || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Bottom Row: Service Breakdown + Recent ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Service Breakdown Pie */}
        <div className="bg-white rounded-3xl border border-[#e1ebf0] shadow-sm p-6">
          <h3 className="text-base font-black text-gray-800 mb-1">Service Breakdown</h3>
          <p className="text-xs text-gray-400 mb-4">All-time bookings by service type</p>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip content={<ServiceTooltip />} />
                <Legend
                  formatter={(value) => (
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#555" }}>{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
              No booking data yet.
            </div>
          )}
        </div>

        {/* Recent Completed */}
        <div className="bg-white rounded-3xl border border-[#e1ebf0] shadow-sm p-6">
          <h3 className="text-base font-black text-gray-800 mb-1">Recent Completed</h3>
          <p className="text-xs text-gray-400 mb-4">Latest paid appointments</p>
          <div className="space-y-3">
            {recent.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-6">No completed appointments yet.</p>
            )}
            {recent.map((apt, i) => (
              <div key={apt._id || i} className="flex items-center justify-between p-3 rounded-2xl bg-[#f8fafb] border border-[#e1ebf0]">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#6992a5] to-[#46735c] flex items-center justify-center text-white text-sm font-black">
                    {apt.ownerName?.charAt(0) || "?"}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-800 truncate max-w-[140px]">{apt.ownerName}</p>
                    <p className="text-xs text-gray-400">
                      {apt.pets?.length} pet{apt.pets?.length !== 1 ? "s" : ""} ·{" "}
                      {new Date(apt.createdAt).toLocaleDateString("en-PH", { month: "short", day: "numeric" })}
                    </p>
                  </div>
                </div>
                <span className="text-sm font-black text-[#46735c]">₱{(apt.totalPrice || 0).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Admin Dashboard Shell ────────────────────────────────────────────────────
const AdminDashboard = ({ user, handleLogout, setActiveTab }) => {
  const isAdmin = user?.role === "admin";
  const [activeSubTab, setActiveSubTab] = useState(isAdmin ? "overview" : "appointments");
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, revenue: 0 });
  const [unreadMessages, setUnreadMessages] = useState(0);

  const [scannedIds, setScannedIds] = useState(null);
  const [petFilter, setPetFilter]   = useState(null);

  // Modals
  const [selectedAppointmentModal, setSelectedAppointmentModal] = useState(null);
  const [rejectModal, setRejectModal] = useState(null); // { apt: object }
  const [rejectReason, setRejectReason] = useState("");
  const [customRejectReason, setCustomRejectReason] = useState("");
  const [rejectError, setRejectError] = useState(null);
  const [zoomImage, setZoomImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const [newAlerts, setNewAlerts] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const knownPendingIds = useRef(new Set());
  const isFirstLoad = useRef(true);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get("/api/appointments", {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      if (res.data.success) {
        const apts = res.data.data;
        const revenue = apts
          .filter(a => a.status === "completed")
          .reduce((acc, curr) => acc + (curr.totalPrice || 0), 0);

        const currentPending = apts.filter(a => a.status === "pending");
        const alertsToAdd = [];
        currentPending.forEach(apt => {
          if (!knownPendingIds.current.has(apt._id)) {
            alertsToAdd.push(apt);
            knownPendingIds.current.add(apt._id);
          }
        });
        if (!isFirstLoad.current && alertsToAdd.length > 0) {
          setNewAlerts(prev => [...prev, ...alertsToAdd]);
          playNotificationSound();
        }
        isFirstLoad.current = false;
        setAppointments(apts);
        setStats({ total: apts.length, pending: currentPending.length, revenue });
      }

      if (isAdmin) {
        const resContacts = await axios.get("/api/contacts", {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        if (resContacts.data.success) {
          setUnreadMessages(resContacts.data.data.filter(c => !c.isRead).length);
        }
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  }, [user.token, isAdmin]);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 15000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const handleApproveAppointment = async (apt) => {
    try {
      setIsProcessing(true);
      await axios.put(`/api/appointments/${apt._id}`, { status: "confirmed" }, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      await fetchStats();
      setSelectedAppointmentModal(null);
      setRejectModal(null);
      setIsProcessing(false);
    } catch (err) {
      setIsProcessing(false);
      alert("Failed to approve appointment: " + (err.response?.data?.message || err.message));
    }
  };

  const handleSetOngoing = async (apt) => {
    try {
      setIsProcessing(true);
      await axios.put(`/api/appointments/${apt._id}`, { status: "ongoing" }, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      await fetchStats();
      setSelectedAppointmentModal(prev => prev ? ({ ...prev, status: "ongoing" }) : null);
      setIsProcessing(false);
    } catch (err) {
      setIsProcessing(false);
      alert("Failed to update status: " + (err.response?.data?.message || err.message));
    }
  };

  const handleTogglePickup = async (apt) => {
    try {
      setIsProcessing(true);
      const nextVal = !apt.isReadyForPickup;
      await axios.put(`/api/appointments/${apt._id}`, { isReadyForPickup: nextVal }, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      await fetchStats();
      setSelectedAppointmentModal(prev => prev ? ({ ...prev, isReadyForPickup: nextVal }) : null);
      setIsProcessing(false);
    } catch (err) {
      setIsProcessing(false);
      alert("Failed to update pickup status: " + (err.response?.data?.message || err.message));
    }
  };

  const handleOpenRejectModal = (apt) => {
    setRejectModal({ apt });
    setRejectReason("");
    setCustomRejectReason("");
    setRejectError(null);
  };

  const handleConfirmReject = async () => {
    if (!rejectModal?.apt) return;
    const finalReason = rejectReason === "Others" ? customRejectReason : rejectReason;
    if (!rejectReason) {
      setRejectError("Please select a reason.");
      return;
    }
    if (rejectReason === "Others" && !customRejectReason.trim()) {
      setRejectError("Please specify your reason in the text field.");
      return;
    }
    setRejectError(null);

    try {
      setIsProcessing(true);
      await axios.put(`/api/appointments/${rejectModal.apt._id}`, { 
        status: "rejected", 
        rejectReason: finalReason, 
        cancelReason: finalReason 
      }, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      await fetchStats();
      setRejectModal(null);
      setSelectedAppointmentModal(null);
      setIsProcessing(false);
    } catch (err) {
      setIsProcessing(false);
      alert("Failed to reject appointment: " + (err.response?.data?.message || err.message));
    }
  };

  const handleScanSuccess = (decodedText) => {
    if (!decodedText || typeof decodedText !== "string") {
      alert("Invalid QR code scanned.");
      return;
    }

    const text = decodedText.trim();

    // 1. Try parsing JSON format: { userId, petId } or { appointmentId }
    if (text.startsWith("{") && text.endsWith("}")) {
      try {
        const parsed = JSON.parse(text);
        if (parsed.petId) {
          setScannedIds({ userId: parsed.userId, petId: parsed.petId });
          return;
        }
        if (parsed.appointmentId || parsed.bookingId) {
          setScannedIds({ appointmentId: parsed.appointmentId || parsed.bookingId });
          return;
        }
      } catch (_) {}
    }

    // 2. Try parsing URL format or relative path
    try {
      const url = text.startsWith("http://") || text.startsWith("https://")
        ? new URL(text)
        : new URL(text, window.location.origin);
      const parts = url.pathname.split("/").filter(Boolean);

      // Matches /pet/:userId/:petId
      if (parts.length >= 3 && parts[0] === "pet") {
        setScannedIds({ userId: parts[1], petId: parts[2] });
        return;
      }

      // Matches /appointment/:appointmentId or /booking/:appointmentId
      if (parts.length >= 2 && (parts[0] === "appointment" || parts[0] === "booking")) {
        setScannedIds({ appointmentId: parts[1] });
        return;
      }
    } catch (_) {}

    // 3. Check for 24-character hexadecimal ObjectId
    if (/^[0-9a-fA-F]{24}$/.test(text)) {
      setScannedIds({ petId: text });
      return;
    }

    alert("Could not read QR code. Make sure it is a valid FurpectTails pet or booking QR.");
  };

  const handleViewBookings = (petName, petType) => {
    setScannedIds(null);
    setPetFilter({ petName, petType });
    setActiveSubTab("appointments");
  };

  const removeAlert = (id) => setNewAlerts(prev => prev.filter(a => a._id !== id));

  const tabs = [
    { id: "overview",      label: "Overview",            Icon: ChartIcon,    adminOnly: true  },
    { id: "appointments",  label: "Appointments",        Icon: CalendarIcon, adminOnly: false },
    { id: "services",      label: "Services",            Icon: ScissorsIcon, adminOnly: true  },
    { id: "products",      label: "Products",            Icon: ShopIcon,          adminOnly: true  },
    { id: "users",         label: "Users",               Icon: UserIcon,          adminOnly: true  },
    { id: "showcase",      label: "Before & After",      Icon: SparklesLocalIcon, adminOnly: true  },
    { id: "inbox",         label: "Live Chat & Tickets", Icon: MessageIcon,       adminOnly: true  },
    { id: "auditlogs",     label: "Audit Logs",          Icon: ListIcon,     adminOnly: true  },
    { id: "forecast",      label: "Forecast",            Icon: TrendIcon,    adminOnly: false },
    { id: "scan",          label: "QR Scan",             Icon: CameraIcon,   adminOnly: false },
  ].filter(t => !t.adminOnly || isAdmin);

  return (
    <div className="min-h-screen bg-[#fbf9f6] text-gray-800 font-sans flex flex-col md:flex-row relative">

      {/* Mobile Top Header */}
      <div className="md:hidden flex items-center justify-between bg-[#1b2d24] text-white px-5 py-4 sticky top-0 z-30 shadow-md">
        <div className="flex items-center gap-3">
          <PawIcon size={22} color="#a6cfd5" />
          <span className="font-black text-white text-base tracking-tight">
            Furpect<span className="text-[#a6cfd5]">{isAdmin ? "Admin" : "Staff"}</span>
          </span>
        </div>
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-1 rounded-lg hover:bg-[#284033] text-gray-300 hover:text-white transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {/* Mobile Sidebar Overlay Backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/45 backdrop-blur-sm z-[45] md:hidden animate-in fade-in duration-200"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── LEFT SIDEBAR ── */}
      <aside className={`fixed md:sticky top-0 left-0 h-screen w-64 bg-[#1b2d24] text-gray-100 flex flex-col z-50 border-r border-[#15231c] transition-transform duration-300 md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Brand / Logo */}
        <div className="p-6 border-b border-[#284033] flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#6992a5] to-[#46735c] rounded-xl flex items-center justify-center shadow-md">
            <PawIcon size={20} color="white" />
          </div>
          <div>
            <h1 className="font-black text-white text-base tracking-tight leading-tight">
              Furpect<span className="text-[#a6cfd5]">{isAdmin ? "Admin" : "Staff"}</span>
            </h1>
            <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">
              {isAdmin ? "Management Portal" : "Staff Portal"}
            </p>
          </div>
        </div>

        {/* Navigation Menu List */}
        <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => { 
                setActiveSubTab(tab.id); 
                if (tab.id !== "appointments") setPetFilter(null); 
                setSidebarOpen(false); 
              }}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                activeSubTab === tab.id
                  ? "bg-[#6992a5] text-white shadow-md scale-[1.02]"
                  : "text-gray-300 hover:text-white hover:bg-[#284033] hover:translate-x-1"
              }`}
            >
              <span className="flex items-center gap-3">
                <tab.Icon size={18} color="currentColor" />
                <span>{tab.label}</span>
              </span>
              {/* Badges for pending updates */}
              {tab.id === "appointments" && stats.pending > 0 && (
                <span className="px-2 py-0.5 bg-amber-500 text-white rounded-full text-[10px] font-black">{stats.pending}</span>
              )}
              {tab.id === "overview" && stats.pending > 0 && (
                <span className="px-2 py-0.5 bg-amber-500 text-white rounded-full text-[10px] font-black">{stats.pending}</span>
              )}
              {(tab.id === "inbox" || tab.id === "messages") && unreadMessages > 0 && (
                <span className="px-2 py-0.5 bg-red-500 text-white rounded-full text-[10px] font-black">{unreadMessages}</span>
              )}
            </button>
          ))}
        </nav>

        {/* User Card & Logout Button */}
        <div className="p-4 border-t border-[#284033] bg-[#14221b]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#2d4b3d] rounded-full flex items-center justify-center font-bold text-[#a6cfd5] shadow-inner">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-white truncate leading-tight">{user.name}</p>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{user.role}</p>
            </div>
          </div>
          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="w-full py-2.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 rounded-xl text-xs font-bold transition-colors border border-red-500/20"
          >
            Log Out
          </button>
        </div>
      </aside>

      {/* ── RIGHT CONTENT CONTAINER ── */}
      <div className="flex-1 min-h-screen flex flex-col min-w-0">

        {/* ── TOP STATS & PAGE HEADER ── */}
        <header className="bg-white border-b border-[#e1ebf0] h-20 px-6 md:px-8 flex items-center justify-between shrink-0 shadow-sm z-30">
          <div>
            <h2 className="text-lg sm:text-2xl font-black text-gray-800 flex items-center gap-2">
              {activeSubTab === "overview" ? "Dashboard Overview" : tabs.find(t => t.id === activeSubTab)?.label}
            </h2>
            <p className="text-[11px] sm:text-xs text-[#8a7658] font-semibold mt-0.5 block">
              {activeSubTab === "overview"
                ? "Here's what's happening at the clinic."
                : `Managing ${tabs.find(t => t.id === activeSubTab)?.label?.toLowerCase()}.`}
            </p>
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            {isAdmin && (
              <div className="flex items-center gap-1.5 sm:gap-2 bg-emerald-50 border border-emerald-100 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-xl">
                <span className="text-[9px] sm:text-[10px] font-bold text-[#8a7658] uppercase tracking-widest hidden sm:inline">Revenue</span>
                <span className="text-xs sm:text-base font-black text-[#46735c]">₱{stats.revenue.toLocaleString()}</span>
              </div>
            )}
            {stats.pending > 0 && (
              <div className="flex items-center gap-1.5 sm:gap-2 bg-amber-50 border border-amber-100 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-xl">
                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-ping" />
                <span className="text-xs font-black text-amber-700">{stats.pending} <span className="hidden sm:inline">pending</span></span>
              </div>
            )}
          </div>
        </header>

        {/* ── CONTENT BODY ── */}
        <main className="flex-1 p-6 md:p-8 overflow-y-auto">
          
          {/* Notification Alerts */}
          <div className="fixed top-24 right-6 z-50 flex flex-col gap-3">
            {newAlerts.map(alert => (
              <div key={`alert-${alert._id}`} className="bg-white rounded-2xl shadow-2xl border-2 border-emerald-200 p-4 w-80 animate-in slide-in-from-right duration-300">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2 text-[#46735c] font-black text-sm">
                    <BellIcon size={18} color="#46735c" className="animate-bounce" /> New Booking Alert!
                  </div>
                  <button onClick={() => removeAlert(alert._id)} className="text-gray-400 hover:text-gray-600 text-lg font-bold">&times;</button>
                </div>
                <p className="text-sm font-bold text-gray-800">{alert.ownerName} requested a booking.</p>
                <p className="text-xs text-gray-500 mb-3">{alert.pets?.length} pet{alert.pets?.length !== 1 ? "s" : ""} · ₱{(alert.totalPrice || 0).toLocaleString()}</p>
                <button
                  onClick={() => { 
                    removeAlert(alert._id); 
                    setActiveSubTab("overview"); 
                    setSelectedAppointmentModal(alert); 
                  }}
                  className="w-full py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold text-xs rounded-xl shadow-md hover:opacity-95 transition-all flex items-center justify-center gap-1.5"
                >
                  <span>⚡ Review &amp; Approve/Reject</span>
                </button>
              </div>
            ))}
          </div>

          {/* Actual tab screen render */}
          <div className={`${activeSubTab === "overview" ? "" : "bg-white rounded-3xl shadow-sm border border-[#e1ebf0] p-4 md:p-8"} min-h-[500px]`}>
            {activeSubTab === "overview" && (
              <AdminOverview 
                appointments={appointments} 
                isAdmin={isAdmin}
                onOpenDetails={(apt) => setSelectedAppointmentModal(apt)}
                onApprove={handleApproveAppointment}
                onOpenReject={handleOpenRejectModal}
              />
            )}
            {activeSubTab === "appointments" && (
              <AdminAppointments
                user={user}
                petFilter={petFilter}
                onClearFilter={() => setPetFilter(null)}
                setActiveTab={setActiveTab}
              />
            )}
            {activeSubTab === "services"  && <AdminServices user={user} />}
            {activeSubTab === "products"  && <AdminProducts user={user} />}
            {activeSubTab === "users"     && <AdminUsers user={user} />}
            {activeSubTab === "showcase"  && <AdminShowcase user={user} />}
            {(activeSubTab === "inbox" || activeSubTab === "livechat" || activeSubTab === "messages") && (
              <AdminSupportInbox user={user} />
            )}
            { activeSubTab === "auditlogs" && <AdminAuditLogs user={user} /> }
            { activeSubTab === "forecast"  && <AdminForecasting appointments={appointments} /> }
            { activeSubTab === "scan"      && <AdminQrScanner onScanSuccess={handleScanSuccess} /> }
          </div>
        </main>
      </div>

      {/* ── APPOINTMENT DETAILS MODAL ── */}
      {selectedAppointmentModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setSelectedAppointmentModal(null); }}>
          <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl relative overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-[#6992a5] to-[#46735c]">
              <div className="flex items-center gap-2 text-white">
                <span className="text-xl">📋</span>
                <h2 className="text-lg font-black">Booking Details &amp; Actions</h2>
              </div>
              <button onClick={() => setSelectedAppointmentModal(null)} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 text-white font-bold flex items-center justify-center transition-colors">×</button>
            </div>
            
            <div className="p-6 overflow-y-auto space-y-6">
              {/* Top summary */}
              <div className="flex justify-between items-start pb-4 border-b border-gray-100">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{selectedAppointmentModal.ownerName}</h3>
                  <p className="text-sm text-gray-500">{selectedAppointmentModal.ownerEmail} • {selectedAppointmentModal.ownerPhone}</p>
                </div>
                <div className="text-right">
                  <span className="text-xs font-bold text-gray-400 uppercase">Total Price</span>
                  <p className="text-2xl font-black text-[#46735c]">₱{(selectedAppointmentModal.totalPrice || 0).toLocaleString()}</p>
                  <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase inline-block mt-1 ${
                    selectedAppointmentModal.status === "pending" ? "bg-amber-100 text-amber-700" :
                    selectedAppointmentModal.status === "confirmed" ? "bg-blue-100 text-blue-700" :
                    selectedAppointmentModal.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                    "bg-red-100 text-red-700"
                  }`}>
                    {selectedAppointmentModal.status}
                  </span>
                </div>
              </div>

              {/* ── RESCHEDULE REQUEST BANNER ── */}
              {selectedAppointmentModal.status === "reschedule_requested" && (
                <div className="p-4 bg-orange-50 border-2 border-orange-300 rounded-2xl space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-black uppercase tracking-wider text-orange-800 flex items-center gap-1.5">
                      🔄 Reschedule Request Pending
                    </p>
                    <span className="text-[10px] font-bold bg-orange-200/60 text-orange-900 px-2.5 py-0.5 rounded-full">
                      Action Required
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div className="bg-white/80 p-3 rounded-xl border border-orange-200">
                      <span className="text-[10px] font-bold text-gray-400 uppercase block mb-0.5">Current Date & Time</span>
                      <p className="font-bold text-gray-600 line-through">
                        {(() => {
                          const firstPet = selectedAppointmentModal.pets?.[0];
                          const d = firstPet?.grooming?.date || firstPet?.hotel?.checkInDate || selectedAppointmentModal.appointmentDate || selectedAppointmentModal.createdAt;
                          const t = firstPet?.grooming?.time || firstPet?.hotel?.checkInTime || selectedAppointmentModal.appointmentTime || "";
                          return `${d ? new Date(d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "N/A"}${t ? ` at ${t}` : ""}`;
                        })()}
                      </p>
                    </div>
                    <div className="bg-white p-3 rounded-xl border-2 border-orange-400 shadow-sm">
                      <span className="text-[10px] font-black text-orange-700 uppercase block mb-0.5">Requesting Reschedule To</span>
                      <p className="font-extrabold text-orange-950">
                        📅 {selectedAppointmentModal.rescheduleRequestedDate ? new Date(selectedAppointmentModal.rescheduleRequestedDate).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "TBD"}
                        {selectedAppointmentModal.rescheduleRequestedTime ? ` at ${selectedAppointmentModal.rescheduleRequestedTime}` : ""}
                      </p>
                    </div>
                  </div>
                  {selectedAppointmentModal.rescheduleReason && (
                    <p className="text-xs text-orange-900 bg-white/70 p-2.5 rounded-xl border border-orange-100">
                      <strong>Reason:</strong> {selectedAppointmentModal.rescheduleReason}
                    </p>
                  )}
                </div>
              )}

              {/* Downpayment info */}
              {(selectedAppointmentModal.paymentMethod || selectedAppointmentModal.referenceNumber) && (
                <div className="rounded-2xl p-4 border border-blue-100 bg-blue-50/60 space-y-2 text-xs">
                  <p className="font-black text-blue-700 uppercase tracking-wider">💳 Payment Details</p>
                  <div className="grid grid-cols-2 gap-2 text-gray-600">
                    <div>Method: <strong className="text-gray-800">{selectedAppointmentModal.paymentMethod || "Cash"}</strong></div>
                    <div>Downpayment: <strong className="text-gray-800">₱{(selectedAppointmentModal.downpaymentAmount || 0).toLocaleString()}</strong></div>
                    {selectedAppointmentModal.referenceNumber && (
                      <div className="col-span-2">Ref #: <strong className="font-mono text-gray-800">{selectedAppointmentModal.referenceNumber}</strong></div>
                    )}
                  </div>
                  {selectedAppointmentModal.paymentScreenshot && (
                    <div className="pt-2">
                      <p className="font-bold text-gray-700 mb-1">Payment Proof Screenshot:</p>
                      <img 
                        src={selectedAppointmentModal.paymentScreenshot} 
                        alt="Payment Proof" 
                        onClick={() => setZoomImage(selectedAppointmentModal.paymentScreenshot)}
                        className="max-h-36 rounded-xl border border-blue-200 cursor-zoom-in bg-white"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Pets info */}
              <div className="space-y-4">
                <p className="text-xs font-black text-gray-400 uppercase tracking-wider">Pets in this booking</p>
                {(selectedAppointmentModal.pets || []).map((pet, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-2xl p-4 border border-gray-200 space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{pet.petType === "cat" ? "🐱" : "🐶"}</span>
                      <div>
                        <h4 className="text-base font-bold text-gray-800">{pet.petName}</h4>
                        <p className="text-xs text-gray-500 capitalize">{pet.serviceType === "both" ? "Hotel & Grooming" : pet.serviceType}</p>
                      </div>
                    </div>

                    {pet.grooming && pet.grooming.service && (
                      <div className="bg-white p-3 rounded-xl border border-gray-100 text-xs grid grid-cols-2 gap-2">
                        <div className="col-span-2 font-bold text-purple-700">✂️ Grooming: {pet.grooming.service}</div>
                        <div>Date: <strong className="text-gray-800">{pet.grooming.date ? new Date(pet.grooming.date).toLocaleDateString("en-PH") : "N/A"}</strong></div>
                        <div>Time: <strong className="text-gray-800">{pet.grooming.time}</strong></div>
                        {pet.grooming.haircutStyle && <div className="col-span-2">Style: <strong>{pet.grooming.haircutStyle}</strong></div>}
                        <div className="col-span-2 font-bold text-gray-700 pt-1 border-t border-gray-100">Price: ₱{(pet.grooming.price || 0).toLocaleString()}</div>
                      </div>
                    )}

                    {pet.hotel && pet.hotel.roomType && (
                      <div className="bg-white p-3 rounded-xl border border-gray-100 text-xs grid grid-cols-2 gap-2">
                        <div className="col-span-2 font-bold text-amber-700">🏨 Hotel: {pet.hotel.roomType} ({pet.hotel.stayDays || 1} day{(pet.hotel.stayDays || 1) > 1 ? "s" : ""})</div>
                        <div>Check-in: <strong className="text-gray-800">{pet.hotel.checkInDate ? new Date(pet.hotel.checkInDate).toLocaleDateString("en-PH") : "N/A"} at {pet.hotel.checkInTime}</strong></div>
                        <div>Check-out: <strong className="text-gray-800">{pet.hotel.checkOutDate ? new Date(pet.hotel.checkOutDate).toLocaleDateString("en-PH") : "N/A"}{pet.hotel.checkOutTime ? ` at ${pet.hotel.checkOutTime}` : ""}</strong></div>
                        {Boolean(pet.hotel.exceedingHours) && (
                          <div className="col-span-2 text-purple-700 font-semibold">Exceeding Time: +{pet.hotel.exceedingHours} hr(s) (₱{(pet.hotel.exceedingFee || pet.hotel.exceedingHours * 50).toLocaleString()})</div>
                        )}
                        <div className="col-span-2 font-bold text-gray-700 pt-1 border-t border-gray-100">Hotel Total: ₱{(pet.hotel.totalPrice || 0).toLocaleString()}</div>
                      </div>
                    )}

                    {pet.health?.vaccinationPhotoUrl && (
                      <div className="bg-white p-3 rounded-xl border border-gray-100 text-xs">
                        <p className="font-bold text-emerald-700 mb-1">💉 Vaccination Record:</p>
                        <img 
                          src={pet.health.vaccinationPhotoUrl} 
                          alt="Vaccine" 
                          onClick={() => setZoomImage(pet.health.vaccinationPhotoUrl)}
                          className="max-h-32 rounded-xl border border-emerald-200 cursor-zoom-in bg-white"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Dynamic Actions in Details Modal */}
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex flex-wrap items-center justify-end gap-2">
              <button 
                onClick={() => setSelectedAppointmentModal(null)} 
                className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-xl text-xs transition-colors"
              >
                Close
              </button>

              {/* IF PENDING (Not Approved) */}
              {selectedAppointmentModal.status === "pending" && (
                <>
                  <button 
                    disabled={isProcessing}
                    onClick={() => handleOpenRejectModal(selectedAppointmentModal)} 
                    className="px-5 py-2.5 bg-red-50 hover:bg-red-600 text-red-600 hover:text-white font-bold rounded-xl text-xs transition-colors border border-red-200"
                  >
                    Reject Booking
                  </button>
                  <button 
                    disabled={isProcessing}
                    onClick={() => handleApproveAppointment(selectedAppointmentModal)} 
                    className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs shadow-md transition-all"
                  >
                    {isProcessing ? "Processing..." : "Approve Booking"}
                  </button>
                </>
              )}

              {/* IF APPROVED (Confirmed or Ongoing) */}
              {(selectedAppointmentModal.status === "confirmed" || selectedAppointmentModal.status === "ongoing") && (
                <>
                  {/* On Going button */}
                  {selectedAppointmentModal.status !== "ongoing" ? (
                    <button 
                      disabled={isProcessing}
                      onClick={() => handleSetOngoing(selectedAppointmentModal)} 
                      className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs shadow-sm transition-all flex items-center gap-1.5"
                    >
                      <span>✂️</span> Mark as On Going
                    </button>
                  ) : (
                    <span className="px-3.5 py-2 bg-blue-100 text-blue-800 font-bold rounded-xl text-xs border border-blue-300 flex items-center gap-1.5">
                      <span>✂️</span> On Going (Active)
                    </span>
                  )}

                  {/* Ready for Pickup button */}
                  <button 
                    disabled={isProcessing}
                    onClick={() => handleTogglePickup(selectedAppointmentModal)} 
                    className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 ${
                      selectedAppointmentModal.isReadyForPickup 
                        ? "bg-purple-100 text-purple-800 border-2 border-purple-300" 
                        : "bg-purple-600 hover:bg-purple-700 text-white"
                    }`}
                  >
                    <span>🛍️</span> {selectedAppointmentModal.isReadyForPickup ? "Ready for Pickup (Marked)" : "Ready for Pickup"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── REJECT BOOKING REASON MODAL ── */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[80] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 w-full max-w-md shadow-2xl relative animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-2xl bg-red-100 text-red-600 flex items-center justify-center font-bold text-xl">
                🚫
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">Reject Booking</h2>
                <p className="text-xs text-gray-400 font-medium">Customer: {rejectModal.apt.ownerName}</p>
              </div>
            </div>
            
            <p className="text-xs text-gray-500 mb-4 bg-red-50 p-3 rounded-xl border border-red-100">
              The customer will receive an email and in-dashboard notification explaining why this booking could not be accepted.
            </p>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Select Rejection Reason <span className="text-red-500">*</span>
                </label>
                <select 
                  className={`w-full px-4 py-3 border rounded-xl outline-none text-xs font-bold text-gray-700 ${rejectError && !rejectReason ? "border-red-400 bg-red-50" : "border-gray-200"}`}
                  value={rejectReason}
                  onChange={e => { setRejectReason(e.target.value); setRejectError(null); }}
                >
                  <option value="">-- Choose reason --</option>
                  <option value="Fully booked / No available slots">Fully booked / No available slots</option>
                  <option value="Incomplete vaccination / medical requirements">Incomplete vaccination / medical requirements</option>
                  <option value="Store closed / maintenance">Store closed / maintenance</option>
                  <option value="Pet requirements not met">Pet requirements not met</option>
                  <option value="Invalid payment or downpayment issue">Invalid payment or downpayment issue</option>
                  <option value="Schedule conflict">Schedule conflict</option>
                  <option value="Others">Others</option>
                </select>
              </div>

              {rejectReason === "Others" && (
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Please specify <span className="text-red-500">*</span></label>
                  <input 
                    type="text"
                    className={`w-full px-4 py-3 border rounded-xl outline-none text-xs ${rejectError && !customRejectReason.trim() ? "border-red-400 bg-red-50" : "border-gray-200"}`}
                    value={customRejectReason}
                    placeholder="Explain why the booking is rejected..."
                    onChange={e => { setCustomRejectReason(e.target.value); setRejectError(null); }}
                  />
                </div>
              )}

              {rejectError && (
                <p className="text-xs text-red-500 font-bold bg-red-50 p-2.5 rounded-xl border border-red-100">⚠️ {rejectError}</p>
              )}
            </div>

            <div className="flex gap-3">
              <button 
                onClick={() => setRejectModal(null)} 
                className="flex-1 py-3 bg-gray-100 text-gray-600 font-bold rounded-xl text-xs hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button 
                disabled={isProcessing}
                onClick={handleConfirmReject} 
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-xs transition-colors shadow-md shadow-red-500/20"
              >
                {isProcessing ? "Processing..." : "Confirm Rejection"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── IMAGE LIGHTBOX ZOOM ── */}
      {zoomImage && (
        <div 
          className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[100] flex items-center justify-center p-4 cursor-zoom-out animate-in fade-in duration-200"
          onClick={() => setZoomImage(null)}
        >
          <div 
            className="relative max-w-4xl max-h-[85vh] flex flex-col items-center justify-center animate-in zoom-in-95 duration-200"
            onClick={e => e.stopPropagation()}
          >
            <button 
              onClick={() => setZoomImage(null)}
              className="absolute -top-12 right-0 bg-white/20 hover:bg-white/30 text-white rounded-full w-10 h-10 flex items-center justify-center text-2xl font-bold transition-all shadow-md"
            >
              ×
            </button>
            <img 
              src={zoomImage} 
              alt="Zoomed Record" 
              className="max-w-full max-h-[80vh] rounded-2xl object-contain shadow-2xl border-4 border-white/10"
            />
            <p className="text-white/60 text-xs mt-3 font-semibold tracking-wider">Click anywhere outside to close</p>
          </div>
        </div>
      )}


      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-8 text-center animate-in zoom-in duration-200">
            <div className="w-12 h-12 bg-red-100 text-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4 text-xl">
              🚪
            </div>
            <h2 className="text-xl font-black text-gray-800 mb-2">Confirm Log Out</h2>
            <p className="text-gray-500 text-sm mb-6">Are you sure you want to log out of the admin panel?</p>
            
            <div className="flex gap-3">
              <button 
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 py-3 px-4 rounded-xl font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors text-sm"
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  setShowLogoutConfirm(false);
                  handleLogout();
                }}
                className="flex-1 py-3 px-4 rounded-xl font-bold text-white bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/30 transition-all text-sm"
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PET INFO MODAL (shown after QR scan) ── */}
      {scannedIds && (
        <PetInfoModal
          userId={scannedIds.userId}
          petId={scannedIds.petId}
          appointmentId={scannedIds.appointmentId}
          userToken={user.token}
          onClose={() => setScannedIds(null)}
          onStatusUpdate={fetchStats}
          onViewBookings={handleViewBookings}
          onEditAppointment={(apt) => {
            setScannedIds(null);
            setSelectedAppointmentModal(apt);
            setActiveSubTab("appointments");
          }}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
